/**
 * Workspace Layout Route
 *
 * Pathless layout route that resolves workspace state before rendering
 * the full app chrome (sidebar, breadcrumb, etc.) around child routes.
 *
 * Uses useWorkspaceResolver to determine the current state and redirects
 * to the appropriate page if workspace is not ready:
 * - setup-pending: webhook hasn't created workspace yet
 * - no-access: user has zero workspaces
 *
 * When resolved, renders AppShellContent which provides the sidebar
 * and main content area with <Outlet />.
 */

import { useCallback } from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Button } from '@loader/ui';

import { useWorkspaceResolver } from '@/hooks/useWorkspaceResolver';
import { AppShellContent } from '@/components/layout/AppShell';
import { FullPageLoader } from '@/components/common';
import { queryKeys } from '@/lib/queryKeys';

export const Route = createFileRoute('/_authenticated/_withWorkspace')({
  component: WorkspaceLayout,
});

function WorkspaceLayout() {
  const state = useWorkspaceResolver();
  const queryClient = useQueryClient();

  const handleRetry = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.resolver });
  }, [queryClient]);

  switch (state.status) {
    case 'loading':
      return <FullPageLoader />;
    case 'error':
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
          <p className="text-lg font-medium">Failed to load workspace</p>
          <p className="text-sm text-muted-foreground">
            Could not connect to the server. Please check your connection and try again.
          </p>
          <Button onClick={handleRetry}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </div>
      );
    case 'setup-pending':
      return <Navigate to="/setup-pending" replace />;
    case 'no-workspaces':
      return <Navigate to="/no-access" replace />;
    case 'resolved':
      return <AppShellContent />;
  }
}
